import { useState, useCallback, useEffect } from 'react';
import { B, radius, space } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Card, Input, Icon, Spinner, ErrorMsg, Modal, ModalFooter, clickable, useToast } from '../ui';

// =============================================================
// SageMergeSettingsPanel — step 2 of docs/specs/sage-transaction-merge.md.
//
// dbo.SageTransactions is rebuilt wholesale on every import today: every row
// gets a new TransactionID, and a re-import is a delete-then-insert over
// whatever date range the export covers. Step 2 replaces that with a targeted
// diff — insert what's genuinely new, soft-delete what's genuinely gone, and
// leave every unchanged row (its id included) completely alone.
//
// THIS PANEL IS THE ONE SWITCH THAT TURNS THAT ON, AND IT IS OFF BY DEFAULT.
// Unlike most flags on this platform, turning it ON is the risky direction —
// it changes how live imports behave, against the general ledger, and the one
// thing this codebase cannot verify from here (see the spec's own "Open
// questions") is whether the identity the diff is built on is actually stable
// across real re-imports. The existing "Changes" column on every import row
// already shows that diff today, with nothing applied — that is the evidence
// to look at before switching this on, not a promise this panel can make.
//
// SETTINGS, NOT CODE, because that is what lets the two blast-radius numbers
// be tuned without a deploy (the SageExcludedAccounts reasoning) — and because
// the switch itself needs to be flippable by someone without a SQL client.
// =============================================================

const noteBox = (tone) => {
  const t = {
    info:    { bg: '#f8fafc', border: '#e2e8f0', color: B.textMd },
    success: { bg: '#f0fdf4', border: '#bbf7d0', color: B.green },
    warn:    { bg: '#fffbeb', border: '#fde68a', color: B.amber },
    error:   { bg: '#fef2f2', border: '#fecaca', color: B.red },
  }[tone] || {};
  return {
    marginTop: 10, padding: '8px 12px', borderRadius: radius.md,
    fontSize: 12, background: t.bg, color: t.color, border: `1px solid ${t.border}`,
  };
};

const SageMergeSettingsPanel = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  // Fetched only once opened — one of several panels on this page, and a
  // collapsed one has been read by nobody (the SageExcludedAccountsPanel rule).
  const { data, loading, error, reload } = useApi(
    useCallback(() => (open ? api.get('settings') : Promise.resolve(null)), [open]),
    [open],
  );

  const enabled = data?.sageMergeImportEnabled === 'true';
  const [absDraft, setAbsDraft] = useState('');
  const [pctDraft, setPctDraft] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState(null);
  const [confirmOn, setConfirmOn] = useState(false);

  // Seed the draft fields from what loaded, so editing one number doesn't
  // require re-typing the other. Runs again whenever the panel reloads its data.
  useEffect(() => {
    if (data) {
      setAbsDraft(data.sageMergeBlastRadiusAbs ?? '500');
      setPctDraft(String(Math.round((Number(data.sageMergeBlastRadiusPct ?? 0.2)) * 1000) / 10));
    }
  }, [data]);

  const saveThresholds = async () => {
    const abs = Number(absDraft);
    const pct = Number(pctDraft);
    if (!Number.isFinite(abs) || abs < 0) { setErr('The row-count limit has to be a non-negative number.'); return; }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) { setErr('The percentage limit has to be between 0 and 100.'); return; }
    setSaving(true); setErr(null);
    try {
      await api.put('settings', {
        sageMergeBlastRadiusAbs: String(abs),
        sageMergeBlastRadiusPct: String(pct / 100),
      });
      toast('Thresholds saved — they apply to the next import.');
      await reload();
    } catch (e) { setErr(e.message || 'Could not save the thresholds'); }
    finally { setSaving(false); }
  };

  const setEnabled = async (next) => {
    setSaving(true); setErr(null);
    try {
      await api.put('settings', { sageMergeImportEnabled: String(next) });
      toast(next
        ? 'Merge mode is on — the next import will apply a targeted diff instead of a wholesale replace.'
        : 'Merge mode is off — imports are back to wholesale insert-then-supersede.');
      await reload();
    } catch (e) { setErr(e.message || 'Could not change the setting'); }
    finally { setSaving(false); }
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <div {...clickable(() => setOpen(!open))} aria-expanded={open}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: enabled ? B.amber : B.tealDk, display: 'inline-flex' }}><Icon name="swap" size={18} /></span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.text }}>
              Targeted import merge (step 2){' '}
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                background: enabled ? `${B.amber}20` : `${B.textLt}20`, color: enabled ? B.amber : B.textLt, marginLeft: 4 }}>
                {enabled ? 'ON' : 'OFF'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: B.textMd }}>
              Whether an import updates the ledger in place, or replaces the whole date range as it does today.
            </div>
          </div>
        </div>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 12, lineHeight: 1.55 }}>
            <strong>Off</strong> (today's behaviour): every import inserts the whole file fresh and
            soft-deletes the prior import's rows over the same date range — every transaction gets a
            new id every time. <strong>On</strong>: an import instead compares itself against what's
            already live and only touches the difference — genuinely new rows are added, genuinely gone
            rows are soft-deleted, and everything unchanged (its id included) is left alone. Either way,
            every import now also writes its rows into a permanent log table — that part is always on
            and carries no risk to what's live.
            <div style={{ marginTop: 6 }}>
              <strong>Read the "Changes" column on a few real imports before turning this on.</strong>{' '}
              It already shows what this mode would do, applying nothing — if it looks like normal,
              believable churn, the merge is likely safe; if it shows the whole file as new on every
              import, something about the identity this relies on doesn't hold on this book yet.
            </div>
          </div>

          {loading && <Spinner label="Loading…" />}
          {!loading && error && <ErrorMsg msg={error} onRetry={reload} />}

          {!loading && data && (
            <>
              <div style={{ display: 'flex', gap: space.md, alignItems: 'center', marginTop: 4 }}>
                <Btn size="sm" disabled={saving} variant={enabled ? 'secondary' : 'primary'}
                  onClick={() => (enabled ? setEnabled(false) : setConfirmOn(true))}>
                  {saving ? 'Saving…' : (enabled ? 'Turn off' : 'Turn on…')}
                </Btn>
              </div>

              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${B.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: B.textMd, marginBottom: 8 }}>
                  Blast-radius limits
                </div>
                <div style={{ fontSize: 12, color: B.textMd, marginBottom: 10, lineHeight: 1.5 }}>
                  An import whose diff would remove more rows than EITHER limit below is held for review
                  instead of applied — it lands as "Needs Confirmation" on the import row, with the diff
                  shown, until an admin reviews it and applies it explicitly. These are guesses with no
                  real import-size data behind them; tune them once real diffs show what ordinary churn
                  on this book looks like.
                </div>
                <div style={{ display: 'flex', gap: space.md, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ width: 140 }}>
                    <Input label="Max rows removed" value={absDraft} onChange={setAbsDraft} placeholder="500" />
                  </div>
                  <div style={{ width: 140 }}>
                    <Input label="Max % of live rows" value={pctDraft} onChange={setPctDraft} placeholder="20" />
                  </div>
                  <Btn size="sm" variant="secondary" disabled={saving} onClick={saveThresholds}>
                    {saving ? 'Saving…' : 'Save thresholds'}
                  </Btn>
                </div>
              </div>

              {err && <div style={noteBox('error')}>{err}</div>}
            </>
          )}
        </div>
      )}

      {confirmOn && (
        <Modal title="Turn on targeted import merge?" onClose={() => setConfirmOn(false)} width={520}>
          <div style={{ fontSize: 13, color: B.text, lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              From the next import onward, dbo.SageTransactions will be updated in place instead of
              rebuilt — this changes what is live in the general ledger.
            </p>
            <div style={noteBox('warn')}>
              Have you looked at the "Changes" column on a real re-import and confirmed it shows
              believable, ordinary churn — not the whole file reading as new every time?
            </div>
            <p style={{ fontSize: 12, color: B.textMd, marginBottom: 0 }}>
              You can turn this back off at any time; nothing about turning it off undoes an import that
              already merged. A large or unusual removal will still be held for review rather than
              applied automatically, regardless.
            </p>
          </div>
          <ModalFooter>
            <Btn variant="secondary" onClick={() => setConfirmOn(false)} disabled={saving}>Cancel</Btn>
            <Btn onClick={() => { setConfirmOn(false); setEnabled(true); }} disabled={saving}>
              {saving ? 'Turning on…' : 'Turn on'}
            </Btn>
          </ModalFooter>
        </Modal>
      )}
    </Card>
  );
};

export default SageMergeSettingsPanel;
